import { useState } from "react";
import { useQuery, useMutation, useIsMutating } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faX } from "@fortawesome/free-solid-svg-icons";
import { withAppHOC } from "../../hoc";
import {
  StyledContentContainer,
  StyledActionItemButtons,
  StyledStatusPill,
  StyledFilters,
  StyledFilterContainer,
  StyledSearchButton,
  StyledResetButton,
  StyledFiltersContainer,
} from "../../components/styled";
import PageHeader from "../../components/PageHeader/PageHeader";
import {
  MessageBox,
  CustomConfirmButton,
  TextInput,
} from "../../components/FormElements";
import Loader from "../../components/Loader/Loader";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import NoData from "../../components/NoData/NoData";
import { TableBuilder } from "../../components/TableElements";
import Pagination from "../../components/Pagination/Pagination";
import API from "../../api";
import { RESPONSE_STATUSES } from "../../constants";
import { getSearchParams } from "../../utils";
import FiltersButton from "../../components/FiltersButton/FiltersButton";

const Support = () => {
  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams("pageNumber") || 1,
    recordsPerPage: getSearchParams("recordsPerPage") || 30,
  });

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const readData = async () => {
    try {
      const response = await API.get(
        `/help/support?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fromDate=${appliedFilters.fromDate}&toDate=${appliedFilters.toDate}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ["support_tickets", paginationData, appliedFilters],
    queryFn: readData,
  });

  const reloadData = async () => await refetch();

  const filterData = async (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
  };

  const clearFilters = async (e) => {
    e.preventDefault();
    setFilters({
      fromDate: "",
      toDate: "",
    });
    setAppliedFilters({
      fromDate: "",
      toDate: "",
    });
  };

  const renderPageHeading = () => (
    <PageHeader
      title="Support Tickets"
      showAddButton={true}
      addLink="create"
      addTitle="Create Support Ticket"
      showReloadData={true}
      reloadData={reloadData}
    />
  );

  if (isLoading) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={isRefetching} />
      {renderPageHeading()}
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledContentContainer graybackground="true">
        <StyledFiltersContainer className={showFilters ? "show" : ""}>
          <StyledFilters>
            <StyledFilterContainer>
              <TextInput
                type="date"
                value={filters.fromDate}
                onChange={(value) => handleFilterChange("fromDate", value)}
                placeholder="From Date"
                disabled={isLoading}
              />
              <TextInput
                type="date"
                value={filters.toDate}
                onChange={(value) => handleFilterChange("toDate", value)}
                placeholder="To Date"
                disabled={isLoading}
              />
            </StyledFilterContainer>
            <StyledSearchButton onClick={filterData} type="button">
              <FontAwesomeIcon icon={faSearch} />
              Search
            </StyledSearchButton>
            <StyledResetButton onClick={clearFilters} type="button">
              <FontAwesomeIcon icon={faX} />
              Clear
            </StyledResetButton>
          </StyledFilters>
        </StyledFiltersContainer>
        {data?.status === RESPONSE_STATUSES.FAILED && (
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={data?.message}
          />
        )}
        {data?.data?.length === 0 ? (
          <NoData />
        ) : (
          <>
            <TableBuilder
              hasFilters={false}
              isLoading={isLoading}
              tableHeadings={[
                {
                  title: "Ticket ID",
                  dataSelector: "id",
                  dataType: "string",
                },
                {
                  title: "Ticket Details",
                  dataSelector: "description",
                  dataType: "string",
                  cellrenderer: (value, item) => (
                    <>
                      <p>
                        <b>Type:</b> {item.type}
                      </p>
                      <p>
                        <b>Sub Type:</b> {item.subtype}
                      </p>
                      <p>
                        <b>Description:</b>{" "}
                        {value.length > 256 ? value.slice(0, 256) : value}
                      </p>
                    </>
                  ),
                },
                {
                  title: "Status",
                  dataSelector: "status",
                  dataType: "string",
                  textAlign: "center",
                  cellrenderer: (value) => (
                    <StyledStatusPill
                      className={
                        value === "Open"
                          ? "green"
                          : value === "Closed"
                          ? "red"
                          : "yellow"
                      }
                    >
                      {value}
                    </StyledStatusPill>
                  ),
                },
                {
                  title: "Remark",
                  dataSelector: "remark",
                  dataType: "string",
                  cellrenderer: (value) => value || "N/A",
                },
                {
                  title: "Created On",
                  dataSelector: "createdOn",
                  dataType: "string",
                },
                {
                  title: "Closed On",
                  dataSelector: "updatedOn",
                  dataType: "string",
                  cellrenderer: (value) => value || "N/A",
                },
              ]}
              tableData={data?.data}
            />
            <Pagination
              totalRecords={data?.totalRecords}
              paginationData={paginationData}
              setPaginationData={setPaginationData}
            />
          </>
        )}
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(Support);
